import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { api } from '../lib/api'
import { validatePassword } from '../lib/password'
import { PasswordChecklist } from '../components/PasswordChecklist'
import { useMessages } from '@/i18n/messages'
import { passwordMessages } from '@/i18n/text/password'
import { resetPasswordMessages } from '@/i18n/text/resetPassword'

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const m = useMessages(resetPasswordMessages)
  const pm = useMessages(passwordMessages)
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) {
      setError(m.invalidLink)
      return
    }
    const pwError = validatePassword(newPassword)
    if (pwError) {
      setError(pwError)
      return
    }
    if (newPassword !== confirm) {
      setError(pm.mismatch)
      return
    }
    setLoading(true)
    try {
      await api.resetPassword(token, newPassword)
      setDone(true)
    } catch (err) {
      setError((err as Error).message || m.resetFailed)
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-2xl font-bold text-slate-900">{m.updatedTitle}</h1>
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-sm text-emerald-900">
          {m.updatedBody}
        </div>
        <button
          onClick={() => navigate({ to: '/login' })}
          className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded px-4 py-2 transition"
        >
          {m.goToLogin}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <h1 className="text-2xl font-bold text-slate-900">{m.title}</h1>
      <p className="mt-2 text-sm text-slate-500">{m.intro}</p>

      {!token && (
        <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {m.missingToken}{' '}
          <Link to="/forgot-password" className="underline">{m.requestNewLink}</Link>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">{m.newPassword}</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
          <PasswordChecklist password={newPassword} confirm={confirm} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">{m.confirmNewPassword}</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !token}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded px-4 py-2 transition"
        >
          {loading ? m.saving : m.saveNewPassword}
        </button>

        <div className="text-center">
          <Link to="/login" className="text-sm text-emerald-700 underline hover:text-emerald-900">
            {m.backToLogin}
          </Link>
        </div>
      </form>
    </div>
  )
}
